// pages/stock/BranchTransferDialog.jsx
import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, CircularProgress, FormControl,
    InputLabel, Select, MenuItem, Typography, Box,
    Alert, Chip, Stack, Divider
} from '@mui/material';
import {
    SwapHoriz as SwapHorizIcon,
    Warehouse as WarehouseIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { stockTransferAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const BranchTransferDialog = ({ open, onClose, stockItem, branches, onSuccess }) => {
    const { t } = useTranslation();
    const { user, isSuperAdmin } = useAuth();
    const [transferMode, setTransferMode] = useState('central_to_branch');

    const userBranchId = user?.branch;

    const getAvailableQuantity = (sourceBranchId) => {
        if (transferMode === 'central_to_branch') {
            // For central-to-branch, we need the central stock quantity
            // This may come from the centralStockItem reference or be passed separately
            return stockItem?.centralStockQuantity || stockItem?.quantity || 0;
        }
        if (!stockItem?.quantities || !sourceBranchId) return 0;
        return stockItem.quantities[sourceBranchId] || 0;
    };

    const formik = useFormik({
        initialValues: {
            sourceBranchId: userBranchId || '',
            destinationBranchId: '',
            quantity: 1,
            notes: '',
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            sourceBranchId: transferMode === 'branch_to_branch'
                ? Yup.string().required(t('stock.sourceBranchRequired', 'Source branch is required'))
                : Yup.string(),
            destinationBranchId: Yup.string()
                .required(t('stock.destinationBranchRequired', 'Destination branch is required'))
                .test(
                    'not-same-as-source',
                    t('stock.cannotTransferToSameBranch', 'Cannot transfer to the same branch'),
                    function (value) {
                        if (transferMode === 'branch_to_branch') {
                            return value !== this.parent.sourceBranchId;
                        }
                        return true;
                    }
                ),
            quantity: Yup.number()
                .min(1, t('stock.minQuantity', 'Quantity must be at least 1'))
                .required(t('stock.quantityRequired', 'Quantity is required')),
            notes: Yup.string().max(500).optional(),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            try {
                if (transferMode === 'central_to_branch') {
                    // Use central stock ID — it could be the item's centralStockItem reference
                    const centralStockId = stockItem?.centralStockItem?._id
                        || stockItem?.centralStockItem
                        || stockItem?._id;

                    await stockTransferAPI.transferCentralToBranch({
                        centralStockId,
                        branchId: values.destinationBranchId,
                        quantity: Number(values.quantity),
                        notes: values.notes || undefined,
                    });
                } else {
                    await stockTransferAPI.transferBranchToBranch({
                        stockItemId: stockItem._id,
                        sourceBranchId: values.sourceBranchId,
                        destinationBranchId: values.destinationBranchId,
                        quantity: Number(values.quantity),
                        notes: values.notes || undefined,
                    });
                }

                toast.success(t('stock.transferSuccess', 'Stock transferred successfully'));
                onSuccess?.();
                handleClose();
            } catch (error) {
                toast.error(
                    error.response?.data?.message ||
                    t('stock.transferError', 'Failed to transfer stock')
                );
            } finally {
                setSubmitting(false);
            }
        },
    });

    const handleClose = () => {
        formik.resetForm();
        setTransferMode('central_to_branch');
        onClose();
    };

    const availableQty = transferMode === 'central_to_branch'
        ? getAvailableQuantity(null)
        : getAvailableQuantity(formik.values.sourceBranchId);

    const destinationBranches = transferMode === 'branch_to_branch'
        ? branches.filter(b => b._id !== formik.values.sourceBranchId)
        : branches;

    const sourceBranches = branches.filter(b => {
        if (!stockItem?.quantities) return false;
        const qty = stockItem.quantities[b._id] || 0;
        return qty > 0;
    });

    // Determine if central-to-branch is available
    const hasCentralStock = !!(stockItem?.centralStockItem || stockItem?.centralStockQuantity);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <SwapHorizIcon color="primary" />
                    <Typography variant="h6">
                        {t('stock.transferStock', 'Transfer Stock')}
                    </Typography>
                </Box>
            </DialogTitle>

            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    {/* Item Info */}
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">
                            {stockItem?.name}
                        </Typography>
                        <Typography variant="body2">
                            {t('stock.unit', 'Unit')}: {stockItem?.unit || 'pcs'}
                        </Typography>
                    </Alert>

                    {/* Transfer Mode Toggle */}
                    <Stack direction="row" spacing={1} mb={3}>
                        <Button
                            variant={transferMode === 'central_to_branch' ? 'contained' : 'outlined'}
                            startIcon={<WarehouseIcon />}
                            onClick={() => {
                                setTransferMode('central_to_branch');
                                formik.setFieldValue('sourceBranchId', '');
                            }}
                            size="small"
                            fullWidth
                            disabled={!hasCentralStock}
                        >
                            {t('stock.centralToBranch', 'Central → Branch')}
                        </Button>
                        <Button
                            variant={transferMode === 'branch_to_branch' ? 'contained' : 'outlined'}
                            startIcon={<SwapHorizIcon />}
                            onClick={() => {
                                setTransferMode('branch_to_branch');
                                formik.setFieldValue('sourceBranchId', userBranchId || '');
                            }}
                            size="small"
                            fullWidth
                        >
                            {t('stock.branchToBranch', 'Branch → Branch')}
                        </Button>
                    </Stack>

                    {!hasCentralStock && transferMode !== 'branch_to_branch' && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {t('stock.noCentralStockLink', 'This item is not linked to central stock. Only branch-to-branch transfers are available.')}
                        </Alert>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {/* Source Branch (only for B2B) */}
                    {transferMode === 'branch_to_branch' && (
                        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                            <InputLabel>{t('stock.sourceBranch', 'Source Branch')}</InputLabel>
                            <Select
                                name="sourceBranchId"
                                value={formik.values.sourceBranchId}
                                onChange={formik.handleChange}
                                label={t('stock.sourceBranch', 'Source Branch')}
                                error={formik.touched.sourceBranchId && !!formik.errors.sourceBranchId}
                                disabled={!isSuperAdmin && !!userBranchId}
                            >
                                {sourceBranches.map((branch) => {
                                    const qty = stockItem?.quantities?.[branch._id] || 0;
                                    const wac = stockItem?.branchWac?.[branch._id] || 0;
                                    return (
                                        <MenuItem key={branch._id} value={branch._id}>
                                            <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                                                <span>{branch.name}</span>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Chip
                                                        label={`${qty} ${stockItem?.unit || 'pcs'}`}
                                                        size="small"
                                                        color={qty > 0 ? 'success' : 'error'}
                                                        variant="outlined"
                                                    />
                                                    {wac > 0 && (
                                                        <Chip
                                                            label={`WAC: ${wac.toFixed(2)}`}
                                                            size="small"
                                                            color="info"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Stack>
                                            </Box>
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {formik.touched.sourceBranchId && formik.errors.sourceBranchId && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                    {formik.errors.sourceBranchId}
                                </Typography>
                            )}
                            {formik.values.sourceBranchId && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {t('stock.availableQuantity', 'Available')}: {availableQty} {stockItem?.unit || 'pcs'}
                                </Typography>
                            )}
                        </FormControl>
                    )}

                    {/* Available Quantity Info (Central mode) */}
                    {transferMode === 'central_to_branch' && (
                        <Alert severity="info" sx={{ mb: 2 }} variant="outlined">
                            {t('stock.centralStockAvailable', 'Central Stock Available')}: {availableQty}
                            {stockItem?.centralStockItem?.averageCost > 0 && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    {t('stock.centralWac', 'Central WAC')}: {(stockItem.centralStockItem.averageCost || 0).toFixed(2)}
                                </Typography>
                            )}
                        </Alert>
                    )}

                    {/* Destination Branch */}
                    <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                        <InputLabel>{t('stock.destinationBranch', 'Destination Branch')}</InputLabel>
                        <Select
                            name="destinationBranchId"
                            value={formik.values.destinationBranchId}
                            onChange={formik.handleChange}
                            label={t('stock.destinationBranch', 'Destination Branch')}
                            error={formik.touched.destinationBranchId && !!formik.errors.destinationBranchId}
                        >
                            {destinationBranches.map((branch) => (
                                <MenuItem key={branch._id} value={branch._id}>
                                    {branch.name} {branch.location ? `- ${branch.location}` : ''}
                                </MenuItem>
                            ))}
                        </Select>
                        {formik.touched.destinationBranchId && formik.errors.destinationBranchId && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {formik.errors.destinationBranchId}
                            </Typography>
                        )}
                    </FormControl>

                    {/* Quantity */}
                    <TextField
                        fullWidth
                        margin="dense"
                        name="quantity"
                        label={t('stock.quantityToTransfer', 'Quantity to Transfer')}
                        type="number"
                        value={formik.values.quantity}
                        onChange={formik.handleChange}
                        error={formik.touched.quantity && !!formik.errors.quantity}
                        helperText={
                            (formik.touched.quantity && formik.errors.quantity) ||
                            `${t('stock.maxAvailable', 'Max available')}: ${availableQty}`
                        }
                        inputProps={{ min: 1, max: availableQty }}
                        sx={{ mb: 2 }}
                    />

                    {/* Notes */}
                    <TextField
                        fullWidth
                        margin="dense"
                        name="notes"
                        label={t('stock.transferNotes', 'Notes (optional)')}
                        multiline
                        rows={2}
                        value={formik.values.notes}
                        onChange={formik.handleChange}
                        error={formik.touched.notes && !!formik.errors.notes}
                        helperText={formik.touched.notes && formik.errors.notes}
                        inputProps={{ maxLength: 500 }}
                    />

                    {/* Warnings */}
                    {formik.values.quantity > availableQty && availableQty > 0 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {t('stock.quantityExceedsAvailable', 'Quantity exceeds available stock!')}
                        </Alert>
                    )}

                    {availableQty === 0 && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {transferMode === 'central_to_branch'
                                ? t('stock.noCentralStockAvailable', 'No central stock available for transfer')
                                : t('stock.noBranchStockAvailable', 'No stock available at the selected source branch')
                            }
                        </Alert>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={
                            formik.isSubmitting ||
                            formik.values.quantity > availableQty ||
                            availableQty === 0
                        }
                        startIcon={formik.isSubmitting ? <CircularProgress size={18} /> : <SwapHorizIcon />}
                    >
                        {t('stock.transfer', 'Transfer')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default BranchTransferDialog;